import { Stack } from "@mui/material";
import { TextEditor, TextInput, useMuiController } from "@sinch/components";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { t } from "i18next";
import React, { FC } from "react";
import { MessageFormFields } from "./formSchema";
import { AvailableTemplateBox } from "./AvailableTemplateBox";
import type { Placeholders } from "./MessageForm";

interface PushContentProps {
  /** When true, the subject input field is disabled or not rendered. */
  disableSubject?: boolean;
  placeholders?: Placeholders;
  allowed?: boolean;
  fillDefaultTemplate?: boolean;
}

/**
 * PushContent renders text inputs for the subject and message.
 * It conditionally renders the subject input based on the disableSubject prop.
 */
export const PushContent: FC<PushContentProps> = ({
  disableSubject,
  placeholders = [],
  allowed,
  fillDefaultTemplate,
}) => {
  const { field: subjectField } = useMuiController<MessageFormFields, "subject">({
    name: "subject",
    disabled: disableSubject || !allowed,
  });
  const { field: messageField } = useMuiController<MessageFormFields, "pushMessage">({
    name: "pushMessage",
    disabled: !allowed,
  });

  return (
    <>
      {allowed && (
        <AvailableTemplateBox
          channel={NotificationChannel.Push}
          fillDefaultTemplate={fillDefaultTemplate}
          onApplyTemplate={({ subject, content }) => {
            subjectField.onChange(subject);
            messageField.onChange(content);
          }}
        />
      )}
      <Stack gap={2} sx={{ opacity: allowed ? 1 : 0.35 }}>
        {!disableSubject && <TextInput fullWidth label={t("Messages.pushTitleInputLabel")} {...subjectField} />}
        <TextEditor
          fullWidth
          label={t("Messages.messageInputLabel")}
          minRows={8}
          multiline
          placeholders={placeholders}
          plainText
          variant="plain"
          {...messageField}
        />
      </Stack>
    </>
  );
};
